import { isDescopeIssuedToken } from '~/utils/descope/session'

/*
 * Single source of truth for the Descope session that authorizes every MFA call.
 *
 * MFA (feature_DescopeMFA) is deliberately independent of the Descope login flow
 * (feature_DescopeAuthFlows): a user must be able to enrol, re-enrol and be challenged
 * whether or not the Descope login screen is the one they signed in on. But MFA itself is
 * implemented entirely against Descope's SDK, so it always needs a Descope-issued refresh
 * token — something a legacy Member Hub login does not, on its own, guarantee.
 *
 * This resolves that token from whichever of two places actually holds one:
 *
 *   1. `userStore.token`, when the primary session is itself Descope-issued. That covers
 *      the Descope login flow, registerViaDescope() (which runs regardless of the login
 *      flag), and the case where Member Hub simply passes Descope's tokens through
 *      because it fronts Descope as its auth layer. Costs no extra round trip.
 *   2. `userStore.descopeSession`, a side-session obtained by ensureDescopeSession() and
 *      held alongside the Member Hub token, for when Member Hub re-issues its own tokens.
 *
 * Callers should gate MFA UI on `hasDescopeSession` rather than on the presence of any
 * refresh token: a Member Hub token object carries a `refresh_token` too, so a bare
 * truthiness check renders MFA controls that then fail at the first Descope call.
 */
export default function useDescopeSession () {
  const userStore = useUserStore()
  const { descopeProjectId } = useRuntimeConfig().public

  const isPrimarySessionDescope = computed(
    () => isDescopeIssuedToken(userStore.token?.access_token, descopeProjectId)
  )

  /*
   * The side-session wins when there is one. It is the more recent Descope credential —
   * minted by an MFA challenge or enrolment — whereas the primary token is whatever
   * established the app session and is only a fallback for reading Descope state.
   */
  const descopeAccessToken = computed(() => userStore.descopeSession?.access_token ||
    (isPrimarySessionDescope.value ? userStore.token?.access_token : '') || '')

  const descopeRefreshToken = computed(() => userStore.descopeSession?.refresh_token ||
    (isPrimarySessionDescope.value ? userStore.token?.refresh_token : '') || '')

  const hasDescopeSession = computed(() => !!descopeRefreshToken.value)

  /*
   * Whether this account can be offered MFA enrolment at all. That is now exactly one
   * question — is there a Descope session to authorize the calls — because every path that
   * can establish a session runs the challenge decision before it lets the user through:
   * proceedAfterFirstFactor for the Descope-native login and its Google/Apple OAuth, the
   * legacy login form's runMfaGate, and resolveMfaAwarePath for the Member Hub SSO callback
   * (all SSO on the native app, Facebook everywhere, Google One-Tap) and account merge.
   *
   * This used to carry a second condition excluding password-less social accounts behind a
   * provider allowlist. That existed because the SSO callback had no MFA gate at all, so
   * enrolling those users switched on a factor their own login then ignored — see the
   * pre-CLUB-769 note in tests/tests/stores/user-mfa-post-login.test.ts. CLUB-769 put the
   * gate in that callback and removed the reason for it; keeping it only hid enrolment, the
   * login challenge and the account-settings two-step section from SSO users who were in
   * fact being challenged.
   *
   * Note this is NOT a claim that a Descope session implies enforcement — it is the two
   * facts together: the session makes the Descope calls possible, and every login path is
   * gated. A sign-in route added later that skips the gate would break that, so it has to
   * wire in the challenge the way the existing ones do.
   */
  const canEnrolMfa = computed(() => hasDescopeSession.value)

  /*
   * Returns a usable Descope refresh token, establishing a side-session first if needed.
   *
   * Fails OPEN — resolves to '' rather than throwing. At this point the user's credentials
   * have already been accepted, and without a Descope session we cannot even ask whether
   * the account has a second factor enrolled. Refusing every login whenever Descope is
   * unreachable would turn a provider outage into a total sign-in outage, which is worse
   * than the alternative. Note this is the opposite of the policy AFTER a challenge has
   * been issued: once Descope confirms a verified phone, the challenge is mandatory and
   * the caller tears the session down if it is not completed.
   *
   * @param {string} loginId
   * @param {string} password
   * @returns {Promise<string>} refresh token, or '' when none could be established
   */
  async function ensureDescopeSession (loginId, password) {
    if (hasDescopeSession.value) return descopeRefreshToken.value
    if (!loginId || !password) return ''

    try {
      const { token } = await useDescopeWebAuth().signInWithPassword(loginId, password)
      userStore.setDescopeSession(token)
      return token.refresh_token || ''
    } catch {
      return ''
    }
  }

  /*
   * Persists a freshly-issued Descope token (from an enrolment or a re-auth challenge)
   * into the side-session, NEVER into `token`.
   *
   * `token` is the credential every API call authenticates with, and it is only valid
   * because the backend accepted whatever established the session. Swapping in a different
   * Descope token — even a legitimate one — replaces a credential the backend has accepted
   * with one it has not. This previously did exactly that whenever the primary session was
   * itself Descope-issued, which is the signup path: registerViaDescope stores a Descope
   * token, enrolment then overwrote it, and every dashboard call 401'd until the
   * interceptor refreshed. Nothing needs the primary session upgraded — the login gate
   * reads Descope's own verifiedPhone, not the token — so it is simply left alone.
   * @param token
   */
  function storeDescopeToken (token) {
    if (!token) return

    userStore.setDescopeSession(token)
  }

  return {
    isPrimarySessionDescope,
    canEnrolMfa,
    storeDescopeToken,
    descopeAccessToken,
    descopeRefreshToken,
    hasDescopeSession,
    ensureDescopeSession
  }
}
